import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { addHttpBreadcrumb } from '@admin-panel/observability';
import { GATEWAY_API_URL, PORTAL_NAME } from '@admin-panel/config/env';
import { getDeviceFingerprint } from '@admin-panel/utils';
import { buildHeaders, getPortalHeadersByName } from '../headers';
import { handleUnauthorizedSessionExpiry } from '../authSession';
import { APIResponseType } from '@admin-panel/types';

const baseQuery = fetchBaseQuery({
  baseUrl: GATEWAY_API_URL,
  prepareHeaders: headers => {
    const at = localStorage.getItem('at');
    const rt = localStorage.getItem('rt');
    const { portalCode, app } = getPortalHeadersByName(PORTAL_NAME);
    return buildHeaders({
      headers,
      accessToken: at,
      refreshToken: rt,
      deviceId: getDeviceFingerprint(),
      portalCode,
      app,
    });
  },
});

export const rtkBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const result = await baseQuery(args, api, extraOptions);
  const requestInfo = typeof args === 'string' ? { url: args, method: 'GET' } : args;
  const request = result.meta?.request;
  const response = result.meta?.response;

  addHttpBreadcrumb({
    transport: 'rtk',
    phase: result.error ? 'error' : 'success',
    request: {
      url: request?.url ?? requestInfo.url,
      method: request?.method ?? requestInfo.method,
      headers: request?.headers ?? requestInfo.headers,
      body: typeof args === 'string' ? undefined : requestInfo.body,
      params: typeof args === 'string' ? undefined : requestInfo.params,
    },
    response: response
      ? {
          status: response.status,
          headers: response.headers,
          body: result.error ? result.error.data : result.data,
        }
      : undefined,
    error: result.error,
  });

  if (result.error) {
    if (result.error.status === 401) {
      handleUnauthorizedSessionExpiry();
    }

    const error = { ...result.error };

    if (result.error.status === 403) {
      error.data = { message: 'You are not allowed to access this resource' };
    }
    if (result.error.status === 404) {
      error.data = {
        message: 'Wrong url configured. Please contact your tech team',
      };
    }
    if (result.error.status === 500) {
      error.data = { message: 'Something went wrong. Please try again later' };
    }

    return { error };
  }

  const backendResponse = result.data as APIResponseType<unknown>;

  if (!backendResponse.success) {
    const error = {
      status: backendResponse.status.code,
      data: backendResponse.status.message,
    };
    return { error };
  }

  result.data = backendResponse.data;

  return result;
};
